/**
 * Schedules List Component
 *
 * Displays list of schedules with CRUD operations.
 * Allows creating new schedules and viewing/editing existing ones.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalendarMonth as CalendarIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import {
  useGetSchedulesQuery,
  useCreateScheduleMutation,
  useDeleteScheduleMutation,
} from '../../api/schedulingApi';
import DuplicateScheduleDialog from './components/DuplicateScheduleDialog';

interface SchedulesListProps {
  studioId: string;
}

const SchedulesList: React.FC<SchedulesListProps> = ({ studioId }) => {
  const navigate = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [scheduleToDuplicate, setScheduleToDuplicate] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { data: schedules, isLoading, error } = useGetSchedulesQuery(studioId);
  const [createSchedule, { isLoading: isCreating }] =
    useCreateScheduleMutation();
  const [deleteSchedule] = useDeleteScheduleMutation();

  const handleCreate = async () => {
    try {
      await createSchedule({
        studioId,
        data: {
          name: 'New Schedule',
          description: '',
        },
      }).unwrap();

      // Schedule created successfully and will appear in the list
      // TODO: Navigate to detail page once it's implemented
      // navigate(`/scheduling/${result.id}`);
    } catch (err) {
      console.error('Failed to create schedule:', err);
    }
  };

  const handleDeleteClick = (scheduleId: string) => {
    setScheduleToDelete(scheduleId);
    setConfirmDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (scheduleToDelete) {
      try {
        await deleteSchedule({
          id: scheduleToDelete,
          studioId,
        }).unwrap();
      } catch (err) {
        console.error('Failed to delete schedule:', err);
      }
    }
    setConfirmDialogOpen(false);
    setScheduleToDelete(null);
  };

  const handleDeleteCancel = () => {
    setConfirmDialogOpen(false);
    setScheduleToDelete(null);
  };

  const handleViewSchedule = (scheduleId: string) => {
    navigate(`/scheduling/${scheduleId}`);
  };

  const handleDuplicateClick = (scheduleId: string, scheduleName: string) => {
    setScheduleToDuplicate({ id: scheduleId, name: scheduleName });
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateSuccess = (newScheduleId: string) => {
    // Navigate to the newly duplicated schedule
    navigate(`/scheduling/${newScheduleId}`);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load schedules. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6">Schedules</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          disabled={isCreating}
        >
          {isCreating ? 'Creating...' : 'Create Schedule'}
        </Button>
      </Box>

      {schedules && schedules.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 3,
          }}
        >
          {schedules.map(schedule => (
            <Card key={schedule.id}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    {schedule.name}
                  </Typography>
                </Box>

                {schedule.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {schedule.description.length > 100
                      ? `${schedule.description.substring(0, 100)}...`
                      : schedule.description}
                  </Typography>
                )}

                <Typography variant="caption" color="text.secondary">
                  Created: {new Date(schedule.created_at).toLocaleDateString()}
                </Typography>
              </CardContent>

              <CardActions>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleViewSchedule(schedule.id)}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() =>
                    handleDuplicateClick(schedule.id, schedule.name)
                  }
                  title="Duplicate schedule"
                >
                  <ContentCopyIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteClick(schedule.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      ) : (
        <Alert severity="info">
          No schedules yet. Click &quot;Create Schedule&quot; to get started.
        </Alert>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Schedule?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this schedule? This will also delete
            all associated classes, rooms, teachers, and preferences. This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Schedule Dialog */}
      {scheduleToDuplicate && (
        <DuplicateScheduleDialog
          open={duplicateDialogOpen}
          onClose={() => setDuplicateDialogOpen(false)}
          scheduleId={scheduleToDuplicate.id}
          scheduleName={scheduleToDuplicate.name}
          studioId={studioId}
          onSuccess={handleDuplicateSuccess}
        />
      )}
    </Box>
  );
};

export default SchedulesList;
